import {type ChangeEvent, type FormEvent, useEffect, useRef, useState} from "react";
import {membership} from "../../lib/Membership.ts";
import {useNavigate} from "react-router-dom";
import {swal_util} from "../../lib/SwalUtil.ts";
import Loading from "../Loading.tsx";
import {frontBaseUrl} from "../../build_constants.ts";
import {Navbar, type NavbarRef} from "../../components/Navbar.tsx";
import { useTranslation } from "react-i18next";

interface ContentProps {
    toggleSidebar: () => void;
}

const ChangePW = ({toggleSidebar}: ContentProps) => {
    const { t } = useTranslation("ui");

    const mainRef = useRef<HTMLElement>(null);
    const navbarRef = useRef<NavbarRef>(null);

    const navigate = useNavigate();
    const [data, setData] = useState<{
        currentPassword: string;
        newPassword: string;
        confirmPassword: string;
        dataLoading: boolean;
    }>({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        dataLoading: false,
    });

    const [topMargin, setTopMargin] = useState("");

    useEffect(() => {
        if (!navbarRef.current) {
            return
        }

        if (!mainRef.current) {
            return
        }

        const navBarOffsetHeight = `${navbarRef.current.offsetHeight}px`;
        const mainTopPadding = mainRef.current
            ? 'computedStyleMap' in mainRef.current
                ? mainRef.current.computedStyleMap().get("padding-top")?.toString() ?? ""
                : getComputedStyle(mainRef.current).paddingTop
            : "";
        const offsetTop = "300px"; // 300px 만큼 view port 사이즈를 줄여 위로 당김

        setTopMargin(`${navBarOffsetHeight} - ${mainTopPadding} - ${offsetTop}`);
    }, [setTopMargin]);

    async function onSubmitChangePWForm(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (data.currentPassword === "" || data.newPassword === "" || data.confirmPassword === "") {
            await swal_util.show_warning(t("changePassword.errors.fillAllFields"));
            return;
        }
        if (data.currentPassword === data.newPassword) {
            await swal_util.show_warning(t("changePassword.errors.sameAsCurrent"));
            return;
        }
        if (data.newPassword !== data.confirmPassword) {
            await swal_util.show_warning(t("changePassword.errors.passwordsDoNotMatch"));
            return;
        }
        try {
            setData(prev => ({...prev, dataLoading: true}));
            await membership.modify(data.currentPassword, data.newPassword, data.confirmPassword);
            await swal_util.show_info(t("changePassword.changedPleaseLoginAgain"));
            await membership.logout();
            navigate(`${frontBaseUrl}/logout`);
        } catch (error) {
            await swal_util.show_error(t("changePassword.errors.failedToChange"));
            setData(prev => ({...prev, dataLoading: false}));
            console.log(error)
        }
        return false;
    }

    const handleBackClick = () => {
        navigate(-1);
    };

    function updateCurrentPassword(e: ChangeEvent<HTMLInputElement>) {
        setData((prev) => ({...prev, currentPassword: e.target.value}))
    }

    function updateNewPassword(e: ChangeEvent<HTMLInputElement>) {
        setData((prev) => ({...prev, newPassword: e.target.value}))
    }

    function updateConfirmPassword(e: ChangeEvent<HTMLInputElement>) {
        setData((prev) => ({...prev, confirmPassword: e.target.value}))
    }


    return (
        <>
            {data.dataLoading && <Loading text={t("changePassword.changingPassword")}/>}
            {
                <main className="d-block col ps-2 pt-3 min-vh-100" style={{position: 'relative'}} ref={mainRef}>
                    <Navbar toggleSidebar={toggleSidebar} ref={navbarRef}/>
                    <article className="blueshift_main center-container" id="wrapper"
                             style={{minHeight: `calc(100vh - ${topMargin} )`}}>
                        <div className="content">
                            <h3 className="text-secondary text-start">{t("changePassword.changeYourPassword")}</h3>
                            <div className="form-box">
                                <form className="custom-validation" id="submit_form" action="#"
                                      onSubmit={onSubmitChangePWForm}>
                                    <div className="mt-3">
                                        <input type="text"
                                               className="form-control rounded-pill bg-light px-4 py-3 input-style"
                                               value={membership.user_id}
                                               disabled
                                               autoComplete="off"/>
                                    </div>

                                    <div className="mt-3">
                                        <input type="password"
                                               className="form-control rounded-pill bg-light px-4 py-3 input-style"
                                               onChange={updateCurrentPassword}
                                               placeholder={t("changePassword.currentPasswordPlaceholder")}
                                               autoComplete="current-password"/>
                                    </div>

                                    <div className="mt-3">
                                        <input type="password"
                                               className="form-control rounded-pill bg-light px-4 py-3 input-style"
                                               onChange={updateNewPassword}
                                               placeholder={t("changePassword.newPasswordPlaceholder")} autoComplete="off"/>
                                    </div>
                                    <div className="my-3">
                                        <input type="password"
                                               className="form-control rounded-pill bg-light px-4 py-3 input-style"
                                               onChange={updateConfirmPassword}
                                               autoComplete="off"
                                               placeholder={t("changePassword.confirmPasswordPlaceholder")}/>
                                    </div>
                                    <div className="row m-auto">
                                        <button type="button" className="button_red rounded-4 col-md-6 change-pw-button"
                                                style={{marginRight: "15px"}}
                                                onClick={handleBackClick}>{t("common.cancel")}
                                        </button>
                                        <button type="submit"
                                                className="button_blue rounded-4 col-md-6 px-4 change-pw-button">{t("common.update")}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                    </article>

                </main>
            }
        </>
    );
}

export default ChangePW;